import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

// Justificativa dos atrasos do Fiscal — mesma mecânica do modal do BPO RH: ao entrar em
// Entregas ou Tarefas Acessórias, o operador vê o que está atrasado na carteira dele
// (entregas do calendário vencidas e obrigações do Acessórias atrasadas) e diz, caso a
// caso, se a causa é INTERNA (nossa) ou EXTERNA (cliente/terceiro), com observação.
// Caso que continua atrasado 2 dias depois volta com a observação anterior à vista
// (revalidação: avançou × continua na mesma). Enquanto houver pendência, a tela fica
// travada atrás do modal.
const API = process.env.REACT_APP_API_BASE;
const ICONE = { entrega: 'fa-calendar-check', acessorias: 'fa-clipboard-check' };
const COR = { entrega: '#00205b', acessorias: '#0e6e86' };
const chaveDe = (i) => `${i.tipo}|${i.referencia}`;

export default function ModalAtrasosFiscal() {
  const [itens, setItens] = useState([]);
  const [respostas, setRespostas] = useState({});   // chave -> {origem, observacao, andamento}
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const vivo = useRef(true);
  useEffect(() => { vivo.current = true; return () => { vivo.current = false; }; }, []);

  const verificar = useCallback(() => {
    const token = localStorage.getItem('access');
    if (!token) return;
    axios.get(`${API}/api/fiscal/atrasos/`, { params: { _: Date.now() }, headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (vivo.current) setItens(r.data.pendentes || []); })
      .catch(() => {});       // fora do ar não trava a tela
  }, []);
  useEffect(() => {
    verificar();
    const timer = setInterval(verificar, 15 * 60 * 1000);
    return () => clearInterval(timer);
  }, [verificar]);

  if (!itens.length) return null;

  const resposta = (i) => respostas[chaveDe(i)] || {};
  const mudar = (i, campo, valor) => setRespostas((r) => ({ ...r, [chaveDe(i)]: { ...r[chaveDe(i)], [campo]: valor } }));
  const completo = (i) => {
    const r = resposta(i);
    return r.origem && (r.observacao || '').trim().length >= 5 && (!i.revalidacao || r.andamento);
  };
  const prontos = itens.filter(completo).length;

  const salvar = () => {
    setSalvando(true); setErro('');
    const corpo = { itens: itens.filter(completo).map((i) => ({ tipo: i.tipo, referencia: i.referencia, ...resposta(i) })) };
    axios.post(`${API}/api/fiscal/atrasos/`, corpo, { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then((r) => { if (vivo.current) { setItens(r.data.pendentes || []); setRespostas({}); } })
      .catch((e) => { if (vivo.current) setErro(e.response?.data?.mensagem || 'Não foi possível registrar'); })
      .finally(() => { if (vivo.current) setSalvando(false); });
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.6)', zIndex: 1080 }}>
      <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
        <div className="modal-content shadow-lg">
          <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
            <div>
              <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                <i className="fas fa-triangle-exclamation me-2" style={{ color: '#00b5cc' }}></i>
                Atrasos na sua carteira — registre a causa
              </h5>
              <small style={{ color: '#6c757d' }}>
                Para cada caso, diga se o atraso é <b>INTERNO</b> (gargalo nosso) ou <b>EXTERNO</b> (cliente/terceiro) e o motivo.
                Caso que continuar atrasado volta a pedir atualização em 2 dias.
              </small>
            </div>
          </div>
          <div className="modal-body">
            {erro && <div className="alert alert-danger py-2" role="button" onClick={() => setErro('')}>{erro}</div>}
            {itens.map((i) => {
              const r = resposta(i);
              return (
                <div key={chaveDe(i)} className="border rounded p-3 mb-3" style={{ borderLeft: `4px solid ${COR[i.tipo]}`, background: completo(i) ? '#f6fffa' : '#fff' }}>
                  <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                    <i className={`fas ${ICONE[i.tipo]}`} style={{ color: COR[i.tipo] }}></i>
                    <b>{i.cliente}</b><span className="text-muted small">({i.cod})</span>
                    <span className="badge bg-danger">venceu há {i.dias_atraso} dia(s)</span>
                    <span className="badge bg-light text-dark border">{i.tipo_rotulo}</span>
                  </div>
                  <div className="small text-muted mb-2">{i.descricao}</div>
                  {i.revalidacao && i.ultima && (
                    <div className="small bg-light border rounded p-2 mb-2">
                      <i className="fas fa-clock-rotate-left me-1"></i>
                      Última observação ({i.ultima.em}, {i.ultima.origem}{i.ultima.operador ? `, ${i.ultima.operador}` : ''}): “{i.ultima.observacao}”
                      <div className="mt-2 d-flex gap-3">
                        {[['avancou', 'Avançou (outra etapa)'], ['mesma', 'Continua na mesma']].map(([v, t]) => (
                          <label key={v} className="form-check-label d-flex align-items-center gap-1">
                            <input type="radio" className="form-check-input" name={`and-${chaveDe(i)}`}
                              checked={r.andamento === v} onChange={() => mudar(i, 'andamento', v)} /> {t}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="d-flex gap-2 flex-wrap">
                    <select className="form-select" style={{ maxWidth: 180 }} value={r.origem || ''} onChange={(e) => mudar(i, 'origem', e.target.value)}>
                      <option value="">Causa…</option>
                      <option value="INTERNO">INTERNO (nosso)</option>
                      <option value="EXTERNO">EXTERNO (cliente/terceiro)</option>
                    </select>
                    <input className="form-control" style={{ flex: '1 1 260px' }} placeholder="O que travou e qual o próximo passo (mín. 5 caracteres)"
                      value={r.observacao || ''} onChange={(e) => mudar(i, 'observacao', e.target.value)} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="modal-footer bg-light">
            <span className="me-auto small text-muted">{prontos} de {itens.length} preenchido(s)</span>
            <button className="btn btn-primary" onClick={salvar} disabled={salvando || prontos === 0}>
              <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>
              {prontos === itens.length ? 'Registrar e liberar a tela' : `Registrar ${prontos} preenchido(s)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
